import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, limit, getDocs, startAfter } from 'firebase/firestore';
import { db } from '../../core/firebase/firebase';
import store from '../../core/productStore';
import { trackCategory } from '../../core/taste';
import { normalizeCategory, catQueryValues } from '../../core/categoryUtils';
import ProductCard from '../../components/ProductCard';
import { IconArrowLeft } from '../../components/Icons';
import categories from './categories';

const GRID_CSS = `
  .cat-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; padding: 8px 10px 10px; }
  @container (min-width: 480px) { .cat-grid { grid-template-columns: repeat(3, 1fr); } }
  @container (min-width: 680px) { .cat-grid { grid-template-columns: repeat(4, 1fr); } }
  @container (min-width: 900px) { .cat-grid { grid-template-columns: repeat(5, 1fr); } }
`;

const SLUG_TO_CAT = {
  'vetements':    'clothing',
  'chaussures':   'shoes',
  'electronique': 'electronics',
  'jeux-video':   'gaming',
  'laptops':      'laptops',
  'cameras':      'cameras',
  'maison':       'home',
  'sport':        'sport',
  'beaute':       'beaute',
  'livres':       'livres',
  'auto':         'auto',
  'bijoux':       'bijoux',
  'musique':      'musique',
  'animaux':      'animaux',
  'bebe':         'bebe',
  'art':          'art',
  'voyage':       'voyage',
  'jardinage':    'jardinage',
  'bricolage':    'bricolage',
  'sante':        'sante',
  'jouets':       'jouets',
  'montres':      'montres',
  'sacs':         'sacs',
  'autre':        'other',
};

const BATCH = 20;

async function fetchBatch(catValue, cursorEbay, cursorUser) {
  const ebayArgs = [collection(db, 'ebay_products'), where('category', '==', catValue)];
  if (cursorEbay) ebayArgs.push(startAfter(cursorEbay));
  ebayArgs.push(limit(BATCH));

  // query both English and Flutter emoji values for user_products
  const userArgs = [collection(db, 'user_products'), where('category', 'in', catQueryValues(catValue))];
  if (cursorUser) userArgs.push(startAfter(cursorUser));
  userArgs.push(limit(BATCH));

  const [ebaySnap, userSnap] = await Promise.all([
    getDocs(query(...ebayArgs)),
    getDocs(query(...userArgs)),
  ]);

  const ebay  = ebaySnap.docs.map(d => ({ id: d.id, ...d.data(), _col: 'ebay_products', isCreator: false })).filter(p => p.available !== false);
  const users = userSnap.docs.map(d => ({ id: d.id, ...d.data(), _col: 'user_products',  isCreator: true  }));

  return {
    products : [...users, ...ebay].sort((a, b) => (b.likes || 0) - (a.likes || 0)),
    nextEbay : ebaySnap.docs.length === BATCH ? ebaySnap.docs.at(-1) : null,
    nextUser : userSnap.docs.length === BATCH ? userSnap.docs.at(-1) : null,
    doneEbay : ebaySnap.docs.length < BATCH,
    doneUser : userSnap.docs.length < BATCH,
  };
}

export default function CategoryPage() {
  const { slug }  = useParams();
  const navigate  = useNavigate();
  const cat       = categories.find(c => c.slug === slug);
  const catValue  = SLUG_TO_CAT[slug];

  const [products,    setProducts]    = useState(() => {
    const cached = store.get().filter(p => normalizeCategory(p.category) === catValue);
    return cached;
  });
  const [loading,     setLoading]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const cur = useRef({ ebay: null, user: null, doneEbay: false, doneUser: false, busy: false });

  // premier chargement
  useEffect(() => {
    if (!cat || !catValue) return;
    cur.current = { ebay: null, user: null, doneEbay: false, doneUser: false, busy: true };

    const fromStore = store.get().filter(p => normalizeCategory(p.category) === catValue);
    if (fromStore.length > 0) {
      setProducts(fromStore);
      // rafraîchit silencieusement
      fetchBatch(catValue, null, null).then(res => {
        cur.current.ebay     = res.nextEbay;
        cur.current.user     = res.nextUser;
        cur.current.doneEbay = res.doneEbay;
        cur.current.doneUser = res.doneUser;
        cur.current.busy     = false;
        store.add(res.products);
        setProducts(res.products);
      }).catch(() => { cur.current.busy = false; });
      return;
    }

    trackCategory(catValue); // enregistre la préférence
    setLoading(true);
    fetchBatch(catValue, null, null).then(res => {
      cur.current.ebay     = res.nextEbay;
      cur.current.user     = res.nextUser;
      cur.current.doneEbay = res.doneEbay;
      cur.current.doneUser = res.doneUser;
      cur.current.busy     = false;
      store.add(res.products);
      setProducts(res.products);
    }).catch(console.error).finally(() => setLoading(false));
  }, [slug]); // eslint-disable-line

  // charger plus au scroll
  const loadMore = () => {
    if (cur.current.busy || (cur.current.doneEbay && cur.current.doneUser)) return;
    cur.current.busy = true;
    setLoadingMore(true);
    fetchBatch(catValue, cur.current.ebay, cur.current.user).then(res => {
      cur.current.ebay     = res.nextEbay ?? cur.current.ebay;
      cur.current.user     = res.nextUser ?? cur.current.user;
      cur.current.doneEbay = res.doneEbay;
      cur.current.doneUser = res.doneUser;
      cur.current.busy     = false;
      store.add(res.products);
      setProducts(prev => {
        const seen = new Set(prev.map(p => p._col + p.id));
        return [...prev, ...res.products.filter(p => !seen.has(p._col + p.id))];
      });
    }).catch(() => { cur.current.busy = false; }).finally(() => setLoadingMore(false));
  };

  useEffect(() => {
    const onScroll = () => {
      if (loading || loadingMore || cur.current.busy) return;
      if (window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 400)
        loadMore();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [loading, loadingMore]); // eslint-disable-line

  if (!cat) return <div style={st.page}><p style={st.empty}>Catégorie introuvable.</p></div>;

  const allDone = cur.current.doneEbay && cur.current.doneUser;

  return (
    <div style={st.page}>
      <style>{GRID_CSS}</style>
      <div style={st.header}>
        <button style={st.backBtn} onClick={() => navigate('/')}>
          <IconArrowLeft size={22} color="#1a1a1a" />
        </button>
        <div style={st.headerIcon}>
          <span style={{ fontSize: 20 }}>{cat.emoji}</span>
        </div>
        <h1 style={st.headerTitle}>{cat.label}</h1>
      </div>

      {!loading && products.length > 0 && (
        <div style={st.countRow}>
          <span style={st.count}>{products.length} produit{products.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {loading ? (
        <div className="cat-grid">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} />)}</div>
      ) : products.length === 0 ? (
        <p style={st.empty}>Aucun produit dans cette catégorie.</p>
      ) : (
        <>
          <div className="cat-grid">
            {products.map(p => <ProductCard key={`${p._col}${p.id}`} product={p} />)}
          </div>
          {loadingMore && (
            <div className="cat-grid">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} />)}</div>
          )}
          {allDone && !loadingMore && (
            <p style={st.end}>— Fin —</p>
          )}
        </>
      )}
    </div>
  );
}

const Skeleton = () => <div style={st.skeleton} />;

const st = {
  page      : { background: '#f7f8fa', minHeight: '100vh', paddingBottom: 80 },
  header    : { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px 10px', background: '#fff', borderBottom: '1px solid #f0f0f0' },
  backBtn   : { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px 4px 4px 0', marginRight: 2 },
  headerIcon: { width: 36, height: 36, borderRadius: 10, background: '#eef0ff', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  headerTitle:{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', margin: 0 },
  countRow  : { padding: '10px 16px 6px' },
  count     : { fontSize: 13, color: '#888', fontWeight: 500 },
  grid      : {},
  skeleton  : { borderRadius: 14, aspectRatio: '1/1', background: 'linear-gradient(90deg,#eee 25%,#f5f5f5 50%,#eee 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' },
  empty     : { textAlign: 'center', color: '#aaa', fontSize: 15, padding: '60px 0' },
  end       : { textAlign: 'center', color: '#ccc', fontSize: 13, padding: '16px 0 24px' },
};
